// piano-frontend/src/components/TopHeader.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Chip, IconButton, Menu, MenuItem, Divider, DialogTitle, DialogContent, DialogActions, Button, useMediaQuery } from '@mui/material';
import Dialog from './AppDialog';
import { Person, AdminPanelSettings, School, MoreVert, Logout, GetApp, IosShare, Settings, DarkModeRounded, LightModeRounded, LightbulbOutlined } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { useDarkMode } from '../App';
import { useDialog } from '../contexts/DialogProvider';
import NotificationBell from './NotificationBell';
import MetronomeIcon from '../metronome/MetronomeIcon';
import { useMetronome } from '../metronome/MetronomeProvider';
import { piano } from '../theme/pianoTheme';

// 모바일 헤더에 표시할 경로별 페이지 제목 (App.js 라우트 기준)
const getPageTitle = (pathname, role) => {
  if (pathname === '/notice/new') return '공지사항 등록';
  if (/^\/notice\/[^/]+\/edit$/.test(pathname)) return '공지사항 수정';
  if (pathname.startsWith('/notice')) return '공지사항';
  if (pathname.startsWith('/practice')) return '연습실 예약';
  if (pathname === '/lesson') return role === 'MEMBER' ? '레슨 신청' : '레슨 시간표';
  if (pathname === '/lesson-requests') return '레슨 신청 관리';
  if (pathname === '/my-schedule') return '내 일정';
  if (pathname === '/myinfo') return '내 정보';
  if (pathname === '/settings') return '설정';
  if (pathname === '/guide') return '기능 안내';
  if (pathname === '/admin') return '관리자 페이지';
  if (pathname === '/activities') return '변경 사항';
  return '피아노울림';
};

function TopHeader({ user, onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // 모바일(600px 미만)에서만 헤더에 페이지 제목을 표시한다. PC는 기존 사용자 정보 헤더 유지
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { toast } = useDialog();
  const metronome = useMetronome();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [anchorEl, setAnchorEl] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    const installed = window.matchMedia('(display-mode: standalone)').matches;
    setIsInstalled(installed);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstalled(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const getRoleInfo = (role) => {
    switch(role) {
      case 'ADMIN':
        return { 
          icon: <AdminPanelSettings sx={{ fontSize: 16 }} />, 
          label: '관리자',
          color: '#f44336'
        };
      case 'TEACHER':
        return { 
          icon: <School sx={{ fontSize: 16 }} />, 
          label: '선생님',
          color: '#2196f3'
        };
      default:
        return { 
          icon: <Person sx={{ fontSize: 16 }} />, 
          label: '회원',
          color: '#4caf50'
        };
    }
  };

  const roleInfo = getRoleInfo(user?.role);

  const roleChip = (
    <Chip
      icon={roleInfo.icon}
      label={roleInfo.label}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.7rem',
        fontWeight: 600,
        bgcolor: roleInfo.color,
        color: '#fff',
        '& .MuiChip-icon': {
          color: '#fff',
          marginLeft: '4px',
        },
      }}
    />
  );

  const getInitial = (name) => {
    return name ? name.charAt(0) : '?';
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleInstallClick = async () => {
    handleMenuClose();

    if (isIOS) {
      setShowIOSDialog(true);
      return;
    }

    if (!deferredPrompt) {
      toast('이미 설치되었거나 설치할 수 없는 환경입니다.', 'info');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      onLogout();
      navigate('/login');
    } catch (err) {
      console.error('로그아웃 실패:', err);
      toast('로그아웃에 실패했습니다.', 'error');
    }
  };

  const showInstallMenu = (isIOS || deferredPrompt) && !isInstalled;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          // 상태바 영역만큼 위로 늘리고 그만큼 패딩으로 밀어, 내용은 그대로 56px 높이를 유지한다
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          pt: 'env(safe-area-inset-top, 0px)',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        {isMobile ? (
          <Typography
            component="h1"
            noWrap
            sx={{ fontSize: '1.125rem', fontWeight: 700, minWidth: 0, mr: 1 }}
          >
            {getPageTitle(pathname, user?.role)}
          </Typography>
        ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: roleInfo.color,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {getInitial(user?.name)}
          </Avatar>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1.2,
                }}
              >
                {user?.name || '사용자'}
              </Typography>
              {roleChip}
            </Box>
            {user?.username && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                }}
              >
                @{user.username}
              </Typography>
            )}
          </Box>
        </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {/* 다크 모드 토글 — 한 번 탭으로 즉시 전환 (설정 페이지에서 헤더로 이동) */}
          <IconButton
            onClick={toggleDarkMode}
            size="small"
            aria-label={darkMode ? '라이트 모드로 전환' : '다크 모드로 전환'}
          >
            {darkMode ? (
              <DarkModeRounded sx={{ fontSize: 21, color: 'secondary.main' }} />
            ) : (
              <LightModeRounded sx={{ fontSize: 21, color: 'warning.main' }} />
            )}
          </IconButton>

          {user?.role === 'MEMBER' && (
            // 메트로놈 — 어느 화면에서든 한 번 탭으로 연다. 재생 중이면 골드 점으로 표시
            <IconButton onClick={metronome.openSheet} size="small" aria-label="메트로놈 열기" sx={{ position: 'relative' }}>
              <MetronomeIcon sx={{ fontSize: 22, color: metronome.playing ? 'secondary.main' : 'text.secondary' }} />
              {metronome.playing && (
                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', bgcolor: 'secondary.main' }} />
              )}
            </IconButton>
          )}

          {user?.role === 'MEMBER' && <NotificationBell />}

          {isMobile ? (
            // 모바일: 사용자 정보가 헤더에서 빠지므로 아바타를 메뉴 버튼으로 사용
            <IconButton onClick={handleMenuOpen} size="small" aria-label="사용자 메뉴" data-coachmark="user-menu" sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: roleInfo.color,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  boxShadow: open ? `0 0 0 2px ${darkMode ? piano.goldBright : piano.gold}` : 'none',
                }}
              >
                {getInitial(user?.name)}
              </Avatar>
            </IconButton>
          ) : (
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            aria-label="사용자 메뉴"
            data-coachmark="user-menu"
            sx={{
              bgcolor: open ? 'action.hover' : 'transparent',
            }}
          >
            <MoreVert />
          </IconButton>
          )}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 180,
              mt: 1,
              borderRadius: 2,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {user?.name}
              </Typography>
              {isMobile && roleChip}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {user?.username}
            </Typography>
          </Box>
          <Divider />

          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate('/guide');
            }}
            sx={{ py: 1.5 }}
          >
            <LightbulbOutlined sx={{ mr: 1, fontSize: 20 }} />
            기능 안내
          </MenuItem>

          <MenuItem
            onClick={handleSettingsClick}
            sx={{ py: 1.5 }}
          >
            <Settings sx={{ mr: 1, fontSize: 20 }} />
            설정
          </MenuItem>
          
          {showInstallMenu && (
            <MenuItem
              onClick={handleInstallClick}
              sx={{
                color: 'primary.main',
                py: 1.5,
              }}
            >
              <GetApp sx={{ mr: 1, fontSize: 20 }} />
              앱 설치
            </MenuItem>
          )}
          {showInstallMenu && <Divider />}

          <MenuItem
            onClick={() => {
              handleMenuClose();
              handleLogout();
            }}
            sx={{
              color: 'error.main',
              py: 1.5,
              '&:hover': {
                bgcolor: 'error.lighter',
              },
            }}
          >
            <Logout sx={{ mr: 1, fontSize: 20 }} />
            로그아웃
          </MenuItem>
        </Menu>
      </Box>

      {/* iOS 설치 안내 다이얼로그 */}
      <Dialog
        open={showIOSDialog}
        onClose={() => setShowIOSDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          📱 앱 설치 방법 (iOS)
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}
              >
                1
              </Box>
              <Typography variant="body2">
                하단 <IosShare sx={{ fontSize: 18, verticalAlign: 'middle', mx: 0.5 }} /> 공유 버튼 탭
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}
              >
                2
              </Box>
              <Typography variant="body2">
                "홈 화면에 추가" 선택
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIOSDialog(false)}>
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TopHeader;